import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getVisits } from '../../api/careRecords';
import { getNotes } from '../../api/notes';
import { LoadingScreen } from '../../components/LoadingScreen';
import { RequestAccess } from '../../components/RequestAccess';
import { useAuth } from '../../context/AuthProvider';
import type { VisitResponse } from '../../api/careRecords';
import type { Note } from '../../api/notes';

interface FamilyFeedProps {
  patientId: number;
}

interface FeedItem {
  id: string;
  type: 'visit' | 'note';
  timestamp: string;
  description: string;
}

function formatTimestamp(ts: string): string {
  return new Date(ts).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const FamilyFeed: React.FC<FamilyFeedProps> = ({ patientId }) => {
  const { patients } = useAuth();

  if (patients.length === 0) {
    return <RequestAccess />;
  }

  const { data: visits, isLoading: visitsLoading } = useQuery({
    queryKey: ['visits', patientId],
    queryFn: () => getVisits(patientId),
    refetchInterval: 30 * 1000,
  });

  const { data: notes, isLoading: notesLoading } = useQuery({
    queryKey: ['notes', patientId, 'family_message'],
    queryFn: () => getNotes(patientId, 'family_message'),
    refetchInterval: 30 * 1000,
  });

  if (visitsLoading || notesLoading) {
    return <LoadingScreen message="Loading activity feed..." />;
  }

  const feedItems: FeedItem[] = [
    ...(visits ?? []).map((v: VisitResponse) => ({
      id: `visit-${v.id}`,
      type: 'visit' as const,
      timestamp: v.arrived_at,
      description: `Carer visit — ${v.carer_name}`,
    })),
    ...(notes ?? []).map((n: Note) => ({
      id: `note-${n.id}`,
      type: 'note' as const,
      timestamp: n.created_at,
      description: n.content,
    })),
  ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: 24, fontFamily: 'Arial, sans-serif' }}>
      <h1 style={{ fontSize: 28, marginBottom: 8 }}>Activity Feed</h1>
      <p style={{ color: '#666', marginBottom: 32 }}>Today's updates for your loved one</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {feedItems.map((item) => (
          <div
            key={item.id}
            style={{ padding: 16, backgroundColor: '#fff', borderRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: '#1976d2' }}>
                {item.type === 'visit' ? 'Care Update' : 'Family Message'}
              </span>
              <span style={{ color: '#999', fontSize: 14 }}>{formatTimestamp(item.timestamp)}</span>
            </div>
            <p style={{ margin: 0, fontSize: 16 }}>{item.description}</p>
          </div>
        ))}
        {feedItems.length === 0 && (
          <p style={{ textAlign: 'center', color: '#999', fontSize: 16 }}>No activity recorded yet today.</p>
        )}
      </div>
    </div>
  );
};
